import type { HealthRecord } from "../types";

const dayMilliseconds = 86_400_000;

export interface NumericReading {
  value: number;
  recordedAt: string;
}

export interface DashboardInsights {
  weight: {
    latest?: NumericReading;
    previous?: NumericReading;
  };
  vitals: {
    latest?: HealthRecord;
    previous?: HealthRecord;
  };
  exercise: {
    currentMinutes: number;
    previousMinutes: number;
  };
  nextAppointment?: HealthRecord;
}

export function buildDashboardInsights(records: HealthRecord[], now: Date): DashboardInsights {
  const chronological = records.toSorted((left, right) => left.recordedAt.localeCompare(right.recordedAt));
  const weights = chronological.filter((record) => record.kind === "weight" && record.weightKilograms !== undefined);
  const vitals = chronological.filter((record) => record.kind === "vitals" && record.systolic !== undefined && record.diastolic !== undefined);

  const weekStart = startOfWeek(now);
  const elapsed = now.getTime() - weekStart.getTime();
  const previousWeekStart = new Date(weekStart.getTime() - 7 * dayMilliseconds);
  const previousComparableEnd = new Date(previousWeekStart.getTime() + elapsed);
  const exercise = chronological.filter((record) => record.kind === "exercise" && record.durationMinutes !== undefined);
  const currentMinutes = sumMinutes(exercise, weekStart, now);
  const previousMinutes = sumMinutes(exercise, previousWeekStart, previousComparableEnd);

  const nextAppointment = chronological.find((record) => record.kind === "appointment" && new Date(record.recordedAt) >= now);

  return {
    weight: {
      latest: asNumericReading(weights.at(-1)),
      previous: asNumericReading(weights.at(-2)),
    },
    vitals: {
      latest: vitals.at(-1),
      previous: vitals.at(-2),
    },
    exercise: { currentMinutes, previousMinutes },
    nextAppointment,
  };
}

function asNumericReading(record: HealthRecord | undefined): NumericReading | undefined {
  return record?.weightKilograms === undefined ? undefined : { value: record.weightKilograms, recordedAt: record.recordedAt };
}

function startOfWeek(date: Date): Date {
  const result = new Date(date);
  const mondayOffset = (result.getDay() + 6) % 7;
  result.setDate(result.getDate() - mondayOffset);
  result.setHours(0, 0, 0, 0);
  return result;
}

function sumMinutes(records: HealthRecord[], start: Date, end: Date): number {
  return records.reduce((sum, record) => {
    const date = new Date(record.recordedAt);
    return date >= start && date < end ? sum + (record.durationMinutes ?? 0) : sum;
  }, 0);
}
